// Черновик карточки: начал заполнять, ушёл, вернулся завтра — набранное на месте.
//
// ЗАЧЕМ. Сделку заводят не за один присест: половину полей знает менеджер, вторую —
// координатор, а между ними обед, звонок и закрытая вкладка. Сегодня незакрытая форма
// теряется целиком, и человек второй раз набирать не станет — заведёт сделку в двух словах
// и допишет «потом». Отсюда половина пустых полей в базе.
//
// ГДЕ ХРАНИМ. В localStorage браузера, а не на сервере. Причина не в лени: черновик — это
// НЕ ЗАПИСЬ. Отправить его на сервер значит завести сущность, у которой нет ни номера, ни
// состояния, ни автора, и потом объяснять, почему она видна в списке. Когда своя схема
// подключится, серверный черновик станет обычной записью в состоянии «черновик» — и вот
// тогда это хранилище останется только для несохранённого между двумя нажатиями.
//
// ЧЕГО ЗДЕСЬ НЕТ. Ни персональных данных сверх того, что человек сам набрал, ни файлов:
// файл в localStorage не влезет, а base64 от скана акта положит вкладку.

import type { Значения } from './types'

const ПРИСТАВКА = 'cir-card-draft'

/** Черновик протухает: набранное позавчера чаще мешает, чем помогает. */
export const СРОК_ЧЕРНОВИКА_МС = 14 * 24 * 60 * 60 * 1000

export interface Черновик {
  значения: Значения
  /** Когда сохранён, ISO. Показывается человеку: «черновик от 28.07.2026, 14:20». */
  сохранён: string
  /** Ревизия записи, на которой черновик начат. Разошлась — запись правил кто-то ещё. */
  ревизия: number
}

export const ключЧерновика = (сущность: string, id: string) => `${ПРИСТАВКА}:${сущность}:${id}`

/**
 * Хранилище. Вынесено в переменную, потому что localStorage бывает недоступен:
 * приватное окно Safari бросает на записи, и падение формы из-за черновика — худший
 * возможный размен.
 */
function хранилище(): Storage | null {
  try {
    const s = globalThis.localStorage
    if (!s) return null
    return s
  } catch {
    return null
  }
}

export function сохранитьЧерновик(
  сущность: string,
  id: string,
  значения: Значения,
  ревизия: number,
  сейчас = new Date(),
): void {
  const s = хранилище()
  if (!s) return
  const тело: Черновик = { значения, сохранён: сейчас.toISOString(), ревизия }
  try {
    s.setItem(ключЧерновика(сущность, id), JSON.stringify(тело))
  } catch {
    // Место кончилось — черновика просто не будет. Форма от этого работать не перестаёт.
  }
}

/** Читает черновик. Протухший и битый — как будто его нет, и он тут же убирается. */
export function прочитатьЧерновик(
  сущность: string,
  id: string,
  сейчас = new Date(),
): Черновик | null {
  const s = хранилище()
  if (!s) return null
  const сырое = s.getItem(ключЧерновика(сущность, id))
  if (!сырое) return null
  try {
    const ч = JSON.parse(сырое) as Черновик
    if (!ч || typeof ч !== 'object' || !ч.значения || !ч.сохранён) throw new Error('битый')
    const возраст = сейчас.getTime() - new Date(ч.сохранён).getTime()
    if (!Number.isFinite(возраст) || возраст > СРОК_ЧЕРНОВИКА_МС) {
      забытьЧерновик(сущность, id)
      return null
    }
    return ч
  } catch {
    забытьЧерновик(сущность, id)
    return null
  }
}

export function забытьЧерновик(сущность: string, id: string): void {
  const s = хранилище()
  if (!s) return
  try {
    s.removeItem(ключЧерновика(сущность, id))
  } catch {
    // Нечего делать: не удалилось — значит и не читалось.
  }
}
